import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  useOrganization,
  useUpdateOrganization,
  useDeleteOrganization,
  useUploadLogo,
  useRemoveLogo,
} from '../../hooks/useOrganizations';
import { useCurrentOrganization } from '../../contexts/OrganizationContext';
import { RequireRole } from '../../components/auth/RequireRole';
import { MfaPolicySettings } from '../../components/settings/MfaPolicySettings';
import { Button, Field, FormSection, Input, SegmentedControl } from '../../ui';
import type { MfaPolicySettings as MfaPolicySettingsType } from '../../types/organization';
import './OrganizationSettingsPage.css';

const DEFAULT_MFA_POLICY: MfaPolicySettingsType = {
  policy: 'disabled',
  required_for_roles: [],
  grace_period_days: 7,
  policy_enacted_at: null,
};

type LogoInputType = 'url' | 'upload';

export function OrganizationSettingsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { clearCurrentOrg, currentOrgId, setCurrentOrgId } = useCurrentOrganization();

  useEffect(() => {
    if (orgId && orgId !== currentOrgId) {
      setCurrentOrgId(orgId);
    }
  }, [orgId, currentOrgId, setCurrentOrgId]);

  const { data: organization, isLoading, error } = useOrganization(orgId);

  const updateOrg = useUpdateOrganization();
  const deleteOrg = useDeleteOrganization();
  const uploadLogo = useUploadLogo();
  const removeLogo = useRemoveLogo();

  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoInputType, setLogoInputType] = useState<LogoInputType>('url');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [auditRetentionDays, setAuditRetentionDays] = useState<number>(365);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (organization && name === '' && organization.name) {
    setName(organization.name);
    setLogoUrl(organization.logo_url || '');
    setLogoPreview(organization.logo_url || null);
    if (typeof organization.audit_retention_days === 'number') {
      setAuditRetentionDays(organization.audit_retention_days);
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (PNG, JPEG, GIF, WebP, or SVG)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveLogo = async () => {
    if (!orgId) return;
    try {
      await removeLogo.mutateAsync(orgId);
      setLogoUrl('');
      setLogoFile(null);
      setLogoPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch {
      /* handled by mutation */
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;

    try {
      if (logoInputType === 'upload' && logoFile) {
        await uploadLogo.mutateAsync({ orgId, file: logoFile });
        setLogoFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      await updateOrg.mutateAsync({
        orgId,
        data: {
          name: name.trim(),
          logo_url: logoInputType === 'url' ? logoUrl.trim() || undefined : undefined,
          audit_retention_days: auditRetentionDays,
        },
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      /* handled by mutation */
    }
  };

  const handleDelete = async () => {
    if (!orgId || deleteConfirmName !== organization?.name) return;

    try {
      await deleteOrg.mutateAsync(orgId);
      clearCurrentOrg();
      navigate('/organizations');
    } catch {
      /* handled by mutation */
    }
  };

  if (isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page__loading">
          <div className="spinner" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="settings-page">
        <div className="settings-page__error">
          <p>Failed to load organization settings</p>
        </div>
      </div>
    );
  }

  const isSaving = updateOrg.isPending || uploadLogo.isPending;

  return (
    <div className="settings-page">
      <header className="settings-page__header">
        <h1>Organization Settings</h1>
        <p className="settings-page__subtitle">
          Manage your organization&rsquo;s identity and access.
        </p>
      </header>

      <form onSubmit={handleSave} className="settings-page__form">
        <FormSection title="General Settings" description="Basic identity for your organization.">
          <Field label="Organization Name" htmlFor="name">
            <Input
              id="name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </Field>

          <Field label="URL Slug" hint="Cannot be changed after creation" htmlFor="slug">
            <Input id="slug" type="text" value={organization.slug} disabled />
          </Field>

          <div className="settings-page__logo-block">
            <label className="settings-page__logo-label">Logo</label>
            <SegmentedControl
              value={logoInputType}
              onChange={setLogoInputType}
              ariaLabel="Logo source"
              items={[
                { value: 'url', label: 'URL' },
                { value: 'upload', label: 'Upload' },
              ]}
            />

            {logoInputType === 'url' ? (
              <>
                <Input
                  id="logoUrl"
                  type="url"
                  value={logoUrl}
                  onChange={e => {
                    setLogoUrl(e.target.value);
                    setLogoPreview(e.target.value || null);
                  }}
                  placeholder="https://example.com/logo.png"
                />
                <span className="settings-page__input-hint">Direct link to an image</span>
              </>
            ) : (
              <div className="settings-page__file-input">
                <input
                  ref={fileInputRef}
                  id="logoFile"
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                  onChange={handleFileChange}
                  className="settings-page__file-hidden"
                />
                <label htmlFor="logoFile" className="settings-page__file-label">
                  {logoFile ? logoFile.name : 'Choose file...'}
                </label>
                <span className="settings-page__input-hint">
                  PNG, JPEG, GIF, WebP, or SVG (max 5MB)
                </span>
              </div>
            )}

            {logoPreview && (
              <div className="settings-page__logo-preview">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  onError={e => (e.currentTarget.style.display = 'none')}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleRemoveLogo}
                  loading={removeLogo.isPending}
                >
                  Remove Logo
                </Button>
              </div>
            )}
          </div>

          {(updateOrg.isError || uploadLogo.isError) && (
            <p className="settings-page__form-error">
              {updateOrg.error?.message || uploadLogo.error?.message || 'Failed to save settings'}
            </p>
          )}

          {saveSuccess && (
            <p className="settings-page__form-success">Settings saved successfully!</p>
          )}

          <div className="settings-page__form-actions">
            <Button type="submit" loading={isSaving} disabled={isSaving || !name.trim()}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </FormSection>

        <FormSection
          title="Audit Log"
          description="How long to keep audit events for this organization. Older events are permanently deleted by a daily retention job."
        >
          <Field
            label="Retention (days)"
            hint="Default 365. Enterprise customers often extend this to 730 or more for SOC 2 evidence."
            htmlFor="auditRetentionDays"
          >
            <Input
              id="auditRetentionDays"
              type="number"
              min={1}
              max={3650}
              value={auditRetentionDays}
              onChange={e => setAuditRetentionDays(Number(e.target.value) || 365)}
            />
          </Field>
        </FormSection>
      </form>

      <RequireRole role="admin">
        <MfaPolicySettings
          orgId={orgId!}
          policy={organization.mfa_policy ?? DEFAULT_MFA_POLICY}
          canManage
        />
      </RequireRole>

      <RequireRole role="owner">
        <div className="settings-page__danger">
          <h2>Danger Zone</h2>
          <div className="settings-page__danger-row">
            <div>
              <h3>Delete Organization</h3>
              <p>
                Once you delete an organization, there is no going back. This will permanently
                delete the organization, all members, and all associated data.
              </p>
            </div>
            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              Delete Organization
            </Button>
          </div>
        </div>
      </RequireRole>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal delete-modal" onClick={e => e.stopPropagation()}>
            <header className="modal__header">
              <h2>Delete Organization</h2>
              <button
                className="modal__close"
                onClick={() => setShowDeleteModal(false)}
                type="button"
              >
                &times;
              </button>
            </header>
            <div className="modal__body">
              <div className="settings-page__delete-warning">
                <AlertTriangle size={20} aria-hidden="true" />
                <p>
                  This action <strong>cannot be undone</strong>. This will permanently delete the{' '}
                  <strong>{organization.name}</strong> organization and remove all member access.
                </p>
              </div>
              <Field
                label={
                  <>
                    Type <strong>{organization.name}</strong> to confirm:
                  </>
                }
                htmlFor="confirmName"
              >
                <Input
                  id="confirmName"
                  type="text"
                  value={deleteConfirmName}
                  onChange={e => setDeleteConfirmName(e.target.value)}
                  placeholder={organization.name}
                />
              </Field>

              {deleteOrg.isError && (
                <p className="settings-page__form-error">
                  {deleteOrg.error?.message || 'Failed to delete organization'}
                </p>
              )}
            </div>
            <footer className="modal__footer">
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={deleteConfirmName !== organization.name || deleteOrg.isPending}
                loading={deleteOrg.isPending}
              >
                {deleteOrg.isPending ? 'Deleting...' : 'Delete Organization'}
              </Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
